/* eslint-disable @typescript-eslint/no-misused-promises */
import {
  ApButtonLink,
  ApIcon,
  ApLoaderDefault,
} from '@alixpartners/ui-components';
import moment from 'moment';
import React, { useEffect } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { Col, Container, Row } from '../WrappedImports';
import {
  fetchPermissions,
  selectPermissions,
  useAppDispatch,
  useAppSelector,
} from '../../../../state';
import { ActivateLicenseModal } from '../ActivateLicenseModal';

import {
  fetchPowerBiPublishedReports,
  patchPowerBiReport,
} from '../../../../utils/api';
import { arrayMove, copyStringToBuffer } from '../../../../utils/common';
import {
  generateEtPowerBiProLicenseMail,
  generateEtsTableauEmbedMail,
} from '../../../../utils/mailTo';

import { IReport, IPermissions } from '../../../../types';
import PageBlock from '../PageBlock';
import { ReportBlock, SharedReportBlock } from '../ReportBlock';

import './ShareSection.scss';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { Loading } from '../../BuildReports/BuildReports.styles';

interface IShareSectionProps {
  reportsList: IReport[];
  sharedReportsList: IReport[];
  powerBiWorkspaceId: string;
  powerBiSharedWorkspaceId: string;
  powerBiEmbeddedCapacityIsEnabled?: boolean;
  isPowerBiEnabled?: boolean;
  userName: string;
  codeName: string;
  powerBiWorkspaceUrl: string;
  isTableauAuthorized?: boolean;
  isApprover?: boolean;
  isUnauthorized: boolean;
  shareReport: (id: string) => Promise<any>;
  removeSharedReport: (id: string) => Promise<any>;
  isSharedReportstTab?: boolean;
}

interface IShareSectionWithNavigateProps {
  permissions: IPermissions;
  navigate: NavigateFunction;
}

type IProps = IShareSectionWithNavigateProps & IShareSectionProps;

interface IShareSectionState {
  reports: IReport[];
  sharedReports: IReport[];
  isSharedReportsNeedRefetch: boolean;
  isLoadingSharedReports: boolean;
  isActivationModalOpen: boolean;
}

class ShareSection extends React.Component<IProps, IShareSectionState> {
  clientReportSiteLink: string = `${window.location.origin}/client-report-site`;
  constructor(props: IProps) {
    super(props);

    const { reportsList, sharedReportsList, isUnauthorized } = props;

    const reportsCopy = reportsList.map((r) => ({ ...r }));
    const sharedReportsCopy = sharedReportsList
      .map((r) => ({ ...r }))
      .sort((a, b) => (a.rank > b.rank ? 1 : b.rank > a.rank ? -1 : 0));

    this.state = {
      reports: reportsCopy,
      sharedReports: sharedReportsCopy,
      isSharedReportsNeedRefetch: false,
      isLoadingSharedReports: false,
      isActivationModalOpen: isUnauthorized,
    };
  }

  renderNoReportsBlock = () => {
    const { isPowerBiEnabled, navigate, isApprover, powerBiWorkspaceUrl } =
      this.props;

    return (
      <div className="share-section__no-reports share-section__no-reports--all">
        <ActivateLicenseModal
          isOpen={this.state.isActivationModalOpen}
          onModalClose={() => {
            this.setState({ isActivationModalOpen: false });
          }}
          powerBiWorkspaceUrl={powerBiWorkspaceUrl}
        />
        <div className="share-section__no-reports__message"></div>
        {isPowerBiEnabled && !isApprover && (
          <div className="share-section__no-reports__link">
            <ApButtonLink
              onClick={() => {
                navigate('/reports/build');
              }}
            >
              Go to build reports
            </ApButtonLink>
          </div>
        )}
      </div>
    );
  };

  handleLinkToInvitePage = () => {
    this.props.navigate('/setup/invite');
  };

  handleEtsMailLink = () => {
    const { userName, codeName } = this.props;
    window.open(generateEtsTableauEmbedMail(userName, codeName), '_blank');
  };

  handleCopyToClipboard = () => {
    copyStringToBuffer(this.clientReportSiteLink);
  };

  handleLinkToReport = (report: IReport) => {
    const {
      isPowerBiEnabled,
      powerBiWorkspaceId,
      powerBiSharedWorkspaceId,
      sharedReportsList,
    } = this.props;

    // Default link for Tableau report
    let reportLink: string = report.reportWebUrl;

    if (isPowerBiEnabled) {
      if (report.isShared) {
        // We search for shared reports in props.sharedReportsList because we can not get recently shared report's id anywhere else atm
        const recentlySharedReport = sharedReportsList.find(
          (r) => r.reportName === report.reportName
        );
        reportLink = `https://app.powerbi.com/groups/${powerBiSharedWorkspaceId}/reports/${
          recentlySharedReport!.id
        }`;
      } else {
        reportLink = `https://app.powerbi.com/groups/${powerBiWorkspaceId}/reports/${report.id}`;
      }
    }

    window.open(reportLink, '_blank');
  };

  handleLinkToReportTab = (report: IReport, tabId: string) => {
    const { isPowerBiEnabled, sharedReportsList } = this.props;

    let reportLink: string = report.reportPages.find(
      (tab) => tab.id === tabId
    )!.webUrl;

    if (isPowerBiEnabled && report.isShared) {
      const recentlySharedReport = sharedReportsList.find(
        (r) => r.reportName === report.reportName
      );

      reportLink = recentlySharedReport!.reportPages.find(
        (tab) => tab.id === tabId
      )!.webUrl;
    }

    window.open(reportLink, '_blank');
  };

  componentDidUpdate(
    prevProps: IShareSectionProps,
    prevState: IShareSectionState
  ) {
    // Set state reports when reports from props are updated
    if (
      JSON.stringify(prevProps.reportsList) !==
      JSON.stringify(this.props.reportsList)
    ) {
      this.setState({ reports: this.props.reportsList });
    }

    if (!prevProps.isSharedReportstTab && this.props.isSharedReportstTab) {
      if (this.state.isSharedReportsNeedRefetch) {
        this.handleSharedReportsRefetch();
      }
    }

    if (
      !prevState.isSharedReportsNeedRefetch &&
      this.state.isSharedReportsNeedRefetch
    ) {
      if (this.props.isSharedReportstTab) {
        this.handleSharedReportsRefetch();
      }
    }
  }

  handleSharedReportsRefetch = async () => {
    try {
      this.setState({ isLoadingSharedReports: true });
      const sharedReports = await fetchPowerBiPublishedReports();

      sharedReports?.sort((a: IReport, b: IReport) =>
        a.rank > b.rank ? 1 : b.rank > a.rank ? -1 : 0
      );

      this.setState({
        isLoadingSharedReports: false,
        isSharedReportsNeedRefetch: false,
        sharedReports,
      });
    } catch (error) {
      this.setState({
        isLoadingSharedReports: false,
        isSharedReportsNeedRefetch: false,
      });
    }
  };

  handleSharedReportsRefetchSilent = async () => {
    const sharedReports = await fetchPowerBiPublishedReports();

    sharedReports?.sort((a: IReport, b: IReport) =>
      a.rank > b.rank ? 1 : b.rank > a.rank ? -1 : 0
    );

    this.setState({
      sharedReports,
    });
  };

  handleDragReportEnd = (result: DropResult) => {
    const { sharedReports } = this.state;
    const { destination, source } = result;

    if (!destination) {
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const modifiedReportsList: IReport[] = arrayMove(
      sharedReports.map((r) => ({ ...r })).filter((r) => r.isShared),
      source.index,
      destination.index
    );

    modifiedReportsList.forEach((r, index) => {
      // if (index < destination.index && !r.rank) {
      if (index < destination.index && r.rank === null) {
        r.rank = index;
      } else if (index < destination.index) {
        r.rank = --destination.index;
      } else if (index === destination.index) {
        r.rank = destination.index;
      } else if (index > destination.index) {
        r.rank = ++destination.index;
      }
    });

    const patchPayload: Array<Partial<IReport>> = modifiedReportsList
      .filter((report, index) => {
        return (
          report.rank !== sharedReports.find((r) => r.id === report.id)!.rank
        );
      })
      .map((r) => ({ id: r.id, rank: r.rank }));

    patchPowerBiReport(patchPayload).then(
      this.handleSharedReportsRefetchSilent
    );
    this.setState({ sharedReports: modifiedReportsList });
  };

  getReportsCount = () => {
    const { isSharedReportstTab } = this.props;
    const { reports, sharedReports } = this.state;

    return isSharedReportstTab
      ? sharedReports.length
      : reports.length - sharedReports.length;
  };

  renderSharedReportsBlock = () => {
    const { removeSharedReport } = this.props;
    const { sharedReports, isLoadingSharedReports } = this.state;

    if (isLoadingSharedReports) {
      return (
        <div className="shared-reports-laoder">
          <ApLoaderDefault />
        </div>
      );
    }

    return sharedReports.map((report, index) => {
      const {
        isPowerBiEnabled,
        permissions: { unsharePBReportEnabled, unshareTReportEnabled },
      } = this.props;

      return (
        <Draggable key={report.id} draggableId={report.id} index={index}>
          {(draggableProvided) => (
            <SharedReportBlock
              innerRef={draggableProvided.innerRef}
              provided={draggableProvided}
              report={report}
              isPowerBiEnabled={!!isPowerBiEnabled}
              handleReportUnshare={removeSharedReport}
              onReportUnshareDone={(unsharedReport: any) => {
                this.setState((prevState) => {
                  let excludeIds: string[] = [];
                  if (isPowerBiEnabled) {
                    const sharedReport = prevState.reports.filter(
                      (r) =>
                        r.reportName === unsharedReport.reportName && r.isShared
                    );
                    excludeIds = sharedReport.map((r) => r.id);
                  }
                  return {
                    sharedReports: isPowerBiEnabled
                      ? prevState.sharedReports.filter(
                          (r) => r.reportName !== unsharedReport.reportName
                        )
                      : prevState.sharedReports.filter(
                          (r) => r.id !== unsharedReport.id
                        ),
                    reports: isPowerBiEnabled
                      ? prevState.reports
                          .filter((r) => !excludeIds.includes(r.id))
                          .map((r) => {
                            if (r.reportName === unsharedReport.reportName) {
                              r.sharedDate = '';
                            }
                            return r;
                          })
                      : prevState.reports.filter(
                          (r) => r.id !== unsharedReport.id
                        ),
                  };
                });
              }}
              isUnshareEnabled={
                !!unsharePBReportEnabled || !!unshareTReportEnabled
              }
              onDisplayNameSave={async (id: string, displayName: string) => {
                await patchPowerBiReport([{ id, displayName }]);
                this.handleSharedReportsRefetchSilent();
              }}
              onDescriptionSave={async (id: string, description: string) => {
                await patchPowerBiReport([{ id, description }]);
                this.handleSharedReportsRefetchSilent();
              }}
            >
              {report.id}
            </SharedReportBlock>
          )}
        </Draggable>
      );
    });
  };

  shareReportAction = (report: any) => {
    const { isPowerBiEnabled, shareReport } = this.props;
    return isPowerBiEnabled
      ? (id: string) => {
          return shareReport(id).then(() => {
            this.setState((prevState) => ({
              reports: prevState.reports.map((r) => {
                if (r.id === report.id) {
                  return {
                    ...r,
                    sharedDate: moment().format(),
                  };
                } else {
                  return r;
                }
              }),
            }));
          });
        }
      : shareReport;
  };

  renderAllReportsBlock = () => {
    const {
      isPowerBiEnabled,
      permissions: { sharePBReportEnabled, shareTReportEnabled },
    } = this.props;
    const { reports } = this.state;
    const sharedReports = reports.filter((report) => {
      return report.isShared;
    });
    return reports.map((report, index) => {
      if (report.isShared) {
        return '';
      }
      const sharedCopy = sharedReports.filter((sReport) => {
        return sReport.reportName === report.reportName;
      });
      if (sharedCopy.length) {
        report.sharedDate = sharedCopy[0].sharedDate;
      }
      return (
        <ReportBlock
          key={report.id}
          report={report}
          handleReportShare={this.shareReportAction(report)}
          onReportShareDone={() => {
            this.setState({
              isSharedReportsNeedRefetch: true,
            });
          }}
          isPowerBiEnabled={isPowerBiEnabled}
          onReportClick={this.handleLinkToReport}
          onReportTabClick={this.handleLinkToReportTab}
          isShareEnabled={sharePBReportEnabled || shareTReportEnabled}
        />
      );
    });
  };

  render() {
    const {
      navigate,
      powerBiEmbeddedCapacityIsEnabled,
      isPowerBiEnabled,
      userName,
      powerBiWorkspaceUrl,
      isApprover,
      permissions: { updatePBReportEnabled, updateTReportEnabled },
      isSharedReportstTab,
    } = this.props;
    const { reports } = this.state;

    if (!reports.length) {
      return this.renderNoReportsBlock();
    }
    return (
      <div className="fluid">
        <Container fluid className="share-section">
          <Row>
            <Col xs={12}>
              <div className="share-section__header">
                {isApprover && isPowerBiEnabled && (
                  <Row>
                    <Col xs={12}>
                      <PageBlock className="margin-bottom margin-top">
                        <div className="build-section__paragraph-header">
                          Your Power BI Pro license
                        </div>
                        <p>
                          You must have a Power BI Pro license to both publish,
                          and view reports on your Power BI Workspace.
                          AlixPartners should have allocated you a license
                          already, any issues use the email button below to
                          request one.
                        </p>
                        <p>
                          If you have never used Power BI before you must
                          activate your license by logging into a Power BI
                          Workspace. Do this now using the Open Power BI
                          Workspace button below.
                        </p>
                        <br />
                        <div className="flex-container">
                          <div className="email-link margin-right-16">
                            <ApButtonLink
                              target="_blank"
                              rel="noopener noreferrer"
                              className="email-link"
                              href={powerBiWorkspaceUrl}
                            >
                              Open Power BI Workspace
                            </ApButtonLink>
                          </div>
                          <div className="email-link">
                            <ApButtonLink
                              target="_blank"
                              rel="noopener noreferrer"
                              className="email-link"
                              href={generateEtPowerBiProLicenseMail(userName)}
                            >
                              <ApIcon
                                iconName="baseline_email_outline"
                                iconSize={24}
                              />
                              Request my Power BI Pro License
                            </ApButtonLink>
                          </div>
                        </div>
                      </PageBlock>
                    </Col>
                  </Row>
                )}
                {powerBiEmbeddedCapacityIsEnabled ? (
                  <p>
                    You are now ready to share your reports. You will need to{' '}
                    <span
                      className="green-link"
                      onClick={this.handleLinkToInvitePage}
                    >
                      invite your colleagues or Client
                    </span>{' '}
                    to be able to view the Report Site.{' '}
                  </p>
                ) : (
                  <p style={{ minWidth: 1136 }}>
                    {isSharedReportstTab ? 'Shared reports' : 'All reports'}(
                    {this.getReportsCount()})
                  </p>
                )}
              </div>

              {reports.length > 0 ? (
                <Row>
                  <Col xs={12}>
                    <DragDropContext
                      onDragEnd={
                        updatePBReportEnabled || updateTReportEnabled
                          ? this.handleDragReportEnd
                          : () => {}
                      }
                    >
                      <Droppable droppableId="reports-list">
                        {(droppableProvided) => (
                          <div
                            ref={droppableProvided.innerRef}
                            {...droppableProvided.droppableProps}
                          >
                            <div className="share-section__reports-headers">
                              {isSharedReportstTab ? (
                                <Row>
                                  <Col xs={1}>
                                    <div className="label">Order</div>
                                  </Col>
                                  <Col xs={2}>
                                    <div className="label">Report Name</div>
                                  </Col>
                                  <Col xs={2}>
                                    <div className="label">Navigation Name</div>
                                  </Col>
                                  <Col xs={3}>
                                    <div className="label">Narrative</div>
                                  </Col>
                                  <Col xs={3}>
                                    <div className="label">Shared</div>
                                  </Col>
                                  <Col xs={1}>
                                    <div className="label float-right">
                                      Remove
                                    </div>
                                  </Col>
                                </Row>
                              ) : (
                                <Row>
                                  <Col xs={isPowerBiEnabled ? 8 : 6}>
                                    <div className="label">Report name</div>
                                  </Col>
                                  {!isPowerBiEnabled && (
                                    <Col xs={2}>
                                      <div className="label">Last update</div>
                                    </Col>
                                  )}
                                  <Col xs={2}>
                                    <div className="label">Last shared</div>
                                  </Col>
                                  <Col xs={2} />
                                </Row>
                              )}
                            </div>
                            {isSharedReportstTab
                              ? this.renderSharedReportsBlock()
                              : this.renderAllReportsBlock()}
                            {droppableProvided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </Col>
                </Row>
              ) : (
                <div className="share-section__no-reports share-section__no-reports--shared">
                  <div className="share-section__no-reports__message">
                    You have no published reports yet.
                  </div>
                  <div className="share-section__no-reports__link">
                    <ApButtonLink
                      onClick={() => {
                        navigate('/reports/build');
                      }}
                    >
                      Go to build reports
                    </ApButtonLink>
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

const ShareSectionWithNavigate = (props: IShareSectionProps) => {
  const navigate = useNavigate();

  const permissionsData = useAppSelector(selectPermissions);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!permissionsData.permissions) {
      dispatch(fetchPermissions());
    }
  }, []);

  if (permissionsData.loading) {
    return <Loading />;
  }

  return (
    <ShareSection
      {...props}
      navigate={navigate}
      permissions={permissionsData.permissions || ({} as any)}
    />
  );
};

export default ShareSectionWithNavigate;
